import React, { useRef } from 'react';
import { IconPhoto } from '@noths/polaris-client-ribbons-design-system';

import * as S from '../PhotoPicker.styles';

interface Props {
  acceptedFileTypes?: string[];
  addMoreLabel: string;
  emptyBody: string;
  emptyTitle: string;
  isEmpty: boolean;
  maxFileSizeBytes?: number;
  onFilesSelected: (files: File[]) => void;
}

const isTypeAllowed = (type: string, acceptedFileTypes?: string[]) => {
  if (acceptedFileTypes && acceptedFileTypes.length > 0) {
    return acceptedFileTypes.includes(type);
  }
  return type.startsWith('image/');
};

export function PhotoPickerDropzone({
  acceptedFileTypes,
  addMoreLabel,
  emptyBody,
  emptyTitle,
  isEmpty,
  maxFileSizeBytes,
  onFilesSelected,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);

  const acceptAttr =
    acceptedFileTypes && acceptedFileTypes.length > 0 ? acceptedFileTypes.join(',') : 'image/*';

  const isFileAllowed = (file: File) => {
    if (!isTypeAllowed(file.type, acceptedFileTypes)) {
      return false;
    }
    if (maxFileSizeBytes != null && file.size > maxFileSizeBytes) {
      return false;
    }
    return true;
  };

  return (
    <S.Dropzone
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        // `dataTransfer.items` only exposes `.type` during dragover (no size),
        // so the cursor hint reflects type only — size is still enforced on drop.
        const fileItems = Array.from(e.dataTransfer.items).filter((item) => item.kind === 'file');
        const hasInvalid = fileItems.some((item) => !isTypeAllowed(item.type, acceptedFileTypes));
        e.dataTransfer.dropEffect = hasInvalid ? 'none' : 'copy';
      }}
      onDrop={(e) => {
        e.preventDefault();
        const accepted = Array.from(e.dataTransfer.files).filter(isFileAllowed);
        if (accepted.length > 0) {
          onFilesSelected(accepted);
        }
      }}
      type="button"
    >
      <IconPhoto height={32} width={32} />
      {isEmpty ? (
        <>
          <strong>{emptyTitle}</strong>
          <span>{emptyBody}</span>
        </>
      ) : (
        <span>{addMoreLabel}</span>
      )}
      <input
        accept={acceptAttr}
        data-testid="photopicker-file-input"
        hidden
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []).filter(isFileAllowed);
          if (files.length) {
            onFilesSelected(files);
          }
          e.target.value = '';
        }}
        ref={inputRef}
        type="file"
      />
    </S.Dropzone>
  );
}
